/* storybook-check-ignore */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import compact from 'lodash/compact';
import { Box, Button, Flex, Link, Text } from '@opendoor/bricks/core';
import * as api from '../api';
import PropertyDetail from './PropertyDetail';
import HowPricingWorks from './HowPricingWorks';
import moment from 'moment-timezone';
import { remainingDaysRoundedToEndTime } from '../../helpers/exclusives/exclusiveListingInfo';
import ChevronDown from './icons/ChevronDown';
import { Listing, ListingWithComputedProperties } from '../../declarations/exclusives/listing';
import GallerySlider from './gallery/GallerySlider';
import { Settings as SliderSettings } from 'react-slick';
import useMedia from '@opendoor/bricks/hooks/useMedia';
import {
  getIsOOMLS,
  getIsSellerOfferExperiment,
  getListingAddressHumanReadable,
  getListingWithComputedProperties,
  getOriginalListingPriceCents,
} from '../../helpers/exclusives/listing';
import dynamic from 'next/dynamic';

const MapRow = dynamic(() => import('./MapRow'), {
  ssr: false,
});

// eslint-disable-next-line @typescript-eslint/no-var-requires
const momentDurationFormatSetup = require('moment-duration-format');
momentDurationFormatSetup(moment);

interface ISchool {
  schoolName: string;
  gradeOverall: string;
}

interface Props {
  listing: ListingWithComputedProperties;
  listings: ListingWithComputedProperties[] | undefined;
}
// Necessary workaround since the react-slick Slider
// doesnt currently work with the initialSlide on the responsive
// settings.
function useInitialSlide(): number {
  const secondBreakpoint = useMedia('(max-width: 1201px)');
  const thirdBreakpoint = useMedia('(max-width: 993px)');
  const fourthBreakpoint = useMedia('(max-width: 601px)');
  if (fourthBreakpoint) {
    return 0;
  }
  if (thirdBreakpoint) {
    return 1;
  }
  if (secondBreakpoint) {
    return 0;
  }
  return 1;
}

const SLIDER_SETTINGS: SliderSettings = {
  centerPadding: '15px',
  slidesToShow: 3,
  autoplay: false,
  infinite: true,
  draggable: true,
  responsive: [
    {
      breakpoint: 1600,
      settings: {
        speed: 300,
        slidesToShow: 2,
        className: 'center',
        centerMode: true,
        centerPadding: '15px',
      },
    },
    {
      breakpoint: 1200,
      settings: {
        speed: 300,
        slidesToShow: 1,
        className: 'center',
        centerMode: true,
        centerPadding: '15px',
      },
    },
    {
      breakpoint: 992,
      settings: {
        speed: 300,
        slidesToShow: 2,
        className: 'center',
        centerMode: true,
        centerPadding: '15px',
      },
    },
    {
      breakpoint: 600,
      settings: {
        speed: 300,
        slidesToShow: 1,
        className: 'center',
        centerMode: true,
        centerPadding: '15px',
      },
    },
  ],
};

function hasExpired({ exclusive_listing_info: { end_time } }: Listing, now = moment()) {
  return end_time && now.isAfter(end_time);
}

const MAX_DESCRIPTION_INITIAL_LENGTH = 160;
const INITIAL_MAX_AMOUNT_OF_SCHOOLS_TO_SHOW = 5;

function reduceDescription(acc: { words: string[]; textLength: number }, word: string) {
  if (acc.textLength < MAX_DESCRIPTION_INITIAL_LENGTH) {
    acc.textLength += word.length + 1;
    acc.words.push(word);
  }
  return acc;
}

function HomeDescription({ description }: { description: string }) {
  const [showFullDescription, setShowFullDescription] = useState(
    () => description.length < MAX_DESCRIPTION_INITIAL_LENGTH,
  );
  const shownDescription = useMemo(() => {
    if (!showFullDescription) {
      return description
        .split(' ')
        .reduce(reduceDescription, { words: [] as string[], textLength: 0 })
        .words.join(' ');
    }
    return description;
  }, [showFullDescription]);
  return (
    <Flex
      as="section"
      pb="2rem"
      borderBottom="1px solid"
      borderColor="neutrals40"
      gridGap="0.5rem"
      flexDirection="column"
      mb="2rem"
    >
      <Text h="h3" fontSize="s3" lineHeight="s3" fontWeight="bold">
        Description
      </Text>
      <Text as="div" fontSize="s1" lineHeight="s0">
        {shownDescription}
        {!showFullDescription && (
          <>
            {'... '}
            <Button
              opacity="0.9"
              fontWeight="normal"
              variant="link"
              as="span"
              onClick={() => setShowFullDescription(true)}
              aria-label={null}
              analyticsName="cosmos-exclusives-home-details-read-more-description-button"
            >
              Read more
            </Button>
          </>
        )}
      </Text>
    </Flex>
  );
}

const HomeDetails: React.FC<Props> = ({ listing, listings }) => {
  const [schools, setSchools] = useState<ISchool[]>();
  const [showSchools, setShowSchools] = useState(false);
  const initialSlide = useInitialSlide();
  const daysRemaining = remainingDaysRoundedToEndTime(listing.exclusive_listing_info) ?? 0;
  const isSellerOfferExperiment = getIsSellerOfferExperiment(listing);
  const isOOMLS = getIsOOMLS(listing);
  const details = compact([
    daysRemaining > 0 ? { title: 'Exclusive days left', value: `${daysRemaining} days` } : null,
    { title: 'Type', value: listing.dwelling_type },
    { title: 'City', value: listing.address.city },
    { title: 'Size', value: listing.square_footage.toLocaleString() },
    { title: 'Built in', value: listing.year_built },
    { title: 'Lot size', value: listing.lot_square_footage.toLocaleString() },
    { title: '# of bathrooms', value: listing.bathrooms },
    listing.listing_price_cents > 0
      ? {
          title: 'Price per sqft',
          value: listing.primary_features.find(([feature]) => feature === '$ / Sq ft')?.[1],
        }
      : null,
    { title: '# of bedrooms', value: listing.bedrooms },
    ...listing.listing_features.map((item) => ({
      title: item.name,
      value: item.value,
    })),
  ]);

  const homeDetails = {
    details,
  };

  const getSchools = useCallback(async () => {
    const response = await api.getPropertySchoolsQuery(
      parseFloat(listing.address.latitude),
      parseFloat(listing.address.longitude),
    );
    if (!response) {
      return;
    }
    setSchools(
      response.geoDataStore?.getNicheSchools?.schools?.filter((school) => school.gradeOverall),
    );
  }, [listing.address.latitude, listing.address.longitude]);

  useEffect(() => {
    getSchools();
  }, [getSchools]);

  const similarListings = useMemo(() => {
    if (!listing.exclusive_listing_info.similar_listings) return [];
    const now = moment();
    return (listing.exclusive_listing_info.similar_listings as Listing[])
      .filter((l) => !hasExpired(l, now))
      .map(getListingWithComputedProperties);
  }, [listing.exclusive_listing_info.similar_listings]);
  return (
    <Box mb="1.75rem" pt="1.25rem">
      {listing.remarks && <HomeDescription description={listing.remarks} />}
      {!listing.exclusive_listing_info.should_obfuscate_address && (
        <Box
          pb="2.5rem"
          borderBottom="1px solid"
          borderColor="neutrals40"
          mb={['2.5rem', '2.5rem', '2.5rem', '3rem']}
        >
          <Flex justifyContent="space-between" alignItems="flex-end">
            <Text h="h3" fontSize="s3" lineHeight="s3" fontWeight="bold">
              Location
            </Text>
            <Link
              fontSize="s0"
              target="_blank"
              color="neutrals100"
              rel="noopener noreferrer"
              textDecoration="underline"
              aria-label={`View ${getListingAddressHumanReadable(listing)} in Google Maps`}
              href={`https://maps.google.com/maps?q=${encodeURIComponent(
                getListingAddressHumanReadable(listing),
              )}`}
              analyticsName="cosmos-exclusives-home-details-google-maps"
            >
              See in Google Maps
            </Link>
          </Flex>
          <MapRow listing={listing} listings={listings} />
        </Box>
      )}
      <Text h="h3" fontSize="s3" lineHeight="s3" fontWeight="bold">
        Features of this home
      </Text>
      <Box
        w="100%"
        pb="2rem"
        mt="2rem"
        mb="2.5rem"
        display="grid"
        gridRowGap="1.5rem"
        gridColumnGap="1rem"
        borderBottom="1px solid"
        borderColor="neutrals40"
        gridTemplateColumns={[
          'repeat(2, 1fr)',
          'repeat(2, 1fr)',
          'repeat(2, 1fr)',
          'repeat(2, 1fr)',
          'repeat(3, 1fr)',
        ]}
      >
        {homeDetails.details.map((detail) => (
          <PropertyDetail
            title={detail.title}
            value={detail.value?.toString() || ''}
            key={detail.title}
          />
        ))}
      </Box>
      {!listing.exclusive_listing_info.should_obfuscate_address && (
        <Box my="1.5rem" borderBottom="1px solid" borderColor="neutrals40" pb="2.5rem">
          <Text h="h3" fontSize="s3" lineHeight="s3" fontWeight="bold">
            Schools
          </Text>
          {!schools?.length ? (
            <Text fontSize="s1" lineHeight="s1" color="neutrals90" mt="1.5rem">
              No schools
            </Text>
          ) : (
            <>
              <Box
                mt="1.5rem"
                display="grid"
                gridRowGap="1rem"
                gridTemplateColumns="minmax(240px, 1fr) 1fr"
              >
                {schools
                  .slice(0, showSchools ? undefined : INITIAL_MAX_AMOUNT_OF_SCHOOLS_TO_SHOW)
                  .map((school) => (
                    <React.Fragment key={school.schoolName}>
                      <Text fontSize="s1" lineHeight="s1" color="neutrals90">
                        {school.schoolName}
                      </Text>
                      <Text fontSize="s1" lineHeight="s1" fontWeight="semibold">
                        {school.gradeOverall}
                      </Text>
                    </React.Fragment>
                  ))}
              </Box>
              {!!schools.slice(INITIAL_MAX_AMOUNT_OF_SCHOOLS_TO_SHOW).length && (
                <Button
                  p="0"
                  display="flex"
                  flexDirection="row"
                  gridGap="6px"
                  mt="1.5rem"
                  width="auto"
                  height="auto"
                  fontSize="s0"
                  lineHeight="s0"
                  // @ts-expect-error - deprecated color
                  color="#0050AA"
                  alignItems="center"
                  textDecoration="none"
                  variant="link"
                  aria-label="All Schools"
                  backgroundColor="transparent"
                  onClick={() => setShowSchools(!showSchools)}
                  _hover={{
                    color: 'var(--colors-brand50)',
                  }}
                  analyticsName="cosmos-exclusives-home-details-school-data"
                >
                  Show more
                  <Box transform={`rotate(${showSchools ? '180deg' : '0deg'})`}>
                    <ChevronDown />
                  </Box>
                </Button>
              )}
              <Text mt="1rem" color="neutrals90" fontSize="s00" lineHeight="s00">
                School data provided by&nbsp;
                <Link
                  target="_blank"
                  color="neutrals90"
                  href="https://www.niche.com"
                  aria-label="Go to learn more about Niche a provider of school data"
                  analyticsName="cosmos-exclusives-home-details-Niche"
                >
                  Niche
                </Link>
              </Text>
            </>
          )}
        </Box>
      )}
      {getOriginalListingPriceCents(listing, isOOMLS) && !isSellerOfferExperiment && !isOOMLS && (
        <HowPricingWorks listing={listing} outsideOfModal />
      )}
      {!!similarListings.length && (
        <Box
          display="flex"
          flexDirection="column"
          overflowX="hidden"
          mt={['2.5rem', '2.5rem', '2.5rem', '3rem']}
        >
          <Text h="h3" fontSize="s3" lineHeight="s3" fontWeight="bold">
            Similar Exclusive{similarListings.length > 1 ? 's' : ''}
          </Text>
          <GallerySlider
            marginY="2rem"
            listings={similarListings}
            analyticsPrefix="similar-homes"
            sharingSource="pdp"
            sliderSettings={{
              ...SLIDER_SETTINGS,
              initialSlide,
              slidesToShow: similarListings.length > 2 ? 3 : similarListings.length,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default HomeDetails;
